// File: frontend/src/pages/Companies/CompanyDetailPage.jsx
// Company detail page with 360-degree view

import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import CompanyForm from '../../components/Companies/CompanyForm';
import { ActivityForm } from '../../components/Activities';
import CompanyAIPanel from '../../components/Companies/CompanyAIPanel';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ClockIcon,
  GlobeAltIcon,
  ChartBarIcon,
  BriefcaseIcon,
  UserIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  // Note: company ids are UUIDs (strings) — do not parseInt() them.

  // Fetch company details
  const { data: company, isLoading, error } = useQuery(
    ['company', id],
    async () => {
      const response = await api.get(`/companies/${id}`);
      return response.data.data; // Fix: access the nested data property
    },
    {
      enabled: !!id && !!user?.id,
    }
  );

  // Delete company mutation
  const deleteMutation = useMutation(
    () => api.delete(`/companies/${id}`),
    {
      onSuccess: () => {
        toast.success('Empresa excluída com sucesso!');
        navigate('/companies');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir empresa');
      },
    }
  );

  const handleDeleteCompany = () => {
    if (window.confirm(`Tem certeza que deseja excluir a empresa ${company.name}?`)) {
      deleteMutation.mutate();
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCompanySize = (size) => {
    const formatMap = {
      'Pequena': 'Pequena Empresa',
      'Média': 'Média Empresa',
      'Grande': 'Grande Empresa'
    };
    return formatMap[size] || size;
  };

  const getSizeIcon = (size) => {
    if (size === 'Grande') return '🏢';
    if (size === 'Média') return '🏬';
    return '🏪';
  };

  const getSizeColor = (size) => {
    const colorMap = {
      'Pequena': 'bg-blue-100 text-blue-800',
      'Média': 'bg-yellow-100 text-yellow-800',
      'Grande': 'bg-green-100 text-green-800'
    };
    return colorMap[size] || 'bg-gray-100 text-gray-800';
  };

  const formatEmployeeCount = (count) => {
    if (!count) return 'Não informado';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K funcionários`;
    return `${count} funcionários`;
  };

  const formatWebsiteUrl = (url) => {
    if (!url) return null;
    return url.replace(/^https?:\/\//, '');
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <div className="text-red-600 mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Erro ao carregar empresa
          </h3>
          <p className="text-gray-600 mb-4">
            {error.response?.data?.message || 'Tente novamente mais tarde'}
          </p>
          <Link to="/companies" className="text-blue-600 hover:underline">
            Voltar para lista de empresas
          </Link>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Empresa não encontrada
          </h3>
          <Link to="/companies" className="text-blue-600 hover:underline">
            Voltar para lista de empresas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/companies"
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {company.name}
              </h1>
              <p className="text-gray-600">
                {company.industry && `${company.industry} • `}
                {formatCompanySize(company.company_size)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowActivityModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2 inline" />
              Adicionar Atividade
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2 inline" />
              Editar
            </button>
            <button
              onClick={handleDeleteCompany}
              disabled={deleteMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4 mr-2 inline" />
              Excluir
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Company Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Header Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start space-x-6">
              {/* Company Icon */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl">{getSizeIcon(company.company_size)}</span>
                </div>
              </div>

              {/* Company Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {company.name}
                  </h2>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSizeColor(company.company_size)}`}>
                    {formatCompanySize(company.company_size)}
                  </span>
                </div>

                <div className="space-y-2">
                  {company.industry && (
                    <p className="text-gray-600 flex items-center">
                      <BriefcaseIcon className="h-4 w-4 mr-2" />
                      {company.industry}
                    </p>
                  )}
                  {company.employee_count && (
                    <p className="text-gray-600 flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      {formatEmployeeCount(company.employee_count)}
                    </p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex items-center space-x-3 mt-4">
                  {company.main_phone && (
                    <a
                      href={`tel:${company.main_phone}`}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      Ligar
                    </a>
                  )}
                  {company.website && (
                    <a
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <GlobeAltIcon className="h-4 w-4 mr-2" />
                      Website
                    </a>
                  )}
                  <Link
                    to={`/contacts?company=${company.id}`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Ver Contatos
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* AI Company Analysis Panel */}
          <CompanyAIPanel company={company} />

          {/* Company Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informações da Empresa
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {company.website && (
                  <div className="flex items-center space-x-3">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Website</p>
                      <a 
                        href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {formatWebsiteUrl(company.website)}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Telefone Principal</p>
                    <p className="text-sm text-gray-600">{company.main_phone || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Indústria</p>
                    <p className="text-sm text-gray-600">{company.industry || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Número de Funcionários</p>
                    <p className="text-sm text-gray-600">{formatEmployeeCount(company.employee_count)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {(company.address || company.city || company.state || company.country) && (
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Endereço</p>
                      <div className="text-sm text-gray-600">
                        {company.address && <p>{company.address}</p>}
                        <p>
                          {[company.city, company.state, company.country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Revenue Anual</p>
                    <p className="text-sm text-gray-600">{formatCurrency(company.annual_revenue)}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <ChartBarIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Tamanho da Empresa</p>
                    <p className="text-sm text-gray-600">{formatCompanySize(company.company_size)}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Criada em</p>
                    <p className="text-sm text-gray-600">
                      {new Date(company.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {company.description && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-2">Descrição</p>
                <p className="text-sm text-gray-600">{company.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Statistics & Contacts */}
        <div className="space-y-6">
          {/* Statistics Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Estatísticas
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserGroupIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-600">Total de Contatos</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {company.total_contacts || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-600">Pipeline Value</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {company.pipeline_value ? formatCurrency(company.pipeline_value) : 'R$ 0'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-gray-600">Oportunidades</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {company.total_opportunities || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-gray-600">Última Atividade</span>
                </div>
                <span className="text-sm text-gray-600">
                  {company.last_activity_date ? 
                    new Date(company.last_activity_date).toLocaleDateString('pt-BR') : 
                    'Nenhuma'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Associated Contacts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Contatos Associados
              </h3>
              <Link
                to={`/contacts/new?company=${company.id}`}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <PlusIcon className="h-4 w-4 inline mr-1" />
                Adicionar
              </Link>
            </div>
            
            <div className="p-6">
              {company.contacts && company.contacts.length > 0 ? (
                <div className="space-y-3">
                  {company.contacts.slice(0, 5).map((contact) => (
                    <div key={contact.id} className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {contact.avatar_url ? (
                          <img
                            className="w-8 h-8 rounded-full"
                            src={contact.avatar_url}
                            alt={`${contact.first_name} ${contact.last_name}`}
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 text-sm font-medium">
                              {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/contacts/${contact.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600"
                        >
                          {contact.first_name} {contact.last_name}
                        </Link>
                        <p className="text-xs text-gray-500 truncate">
                          {contact.title || contact.email}
                        </p>
                      </div>
                    </div>
                  ))}
                  {company.contacts.length > 5 && (
                    <div className="pt-2">
                      <Link
                        to={`/contacts?company=${company.id}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Ver todos os {company.contacts.length} contatos
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Nenhum contato associado
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Adicione contatos para esta empresa.
                  </p>
                  <Link
                    to={`/contacts/new?company=${company.id}`}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Adicionar Contato
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Company Modal */}
      <CompanyForm
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        company={company}
        onSuccess={() => {
          queryClient.invalidateQueries(['company', id]);
          queryClient.invalidateQueries('companies');
          setShowEditModal(false);
        }}
      />
      
      {/* Activity Form Modal */}
      <ActivityForm
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        companyId={id}
        onSuccess={() => {
          queryClient.invalidateQueries(['company', id]);
          queryClient.invalidateQueries('activities');
          setShowActivityModal(false);
        }}
      />
    </div>
  );
};

export default CompanyDetailPage;